import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import type { Session } from '@popcart/core/v1';
import { api, ApiRequestError, errorMessage } from '../api';
import { useAuth } from './AuthProvider';
import { GoogleButton, googleClientId } from './GoogleButton';
import { AuthPage, useNow, useTitle } from '../ui/bits';
import { usePrefs } from '../ui/prefs';
import { landingFor } from '../lib/roles.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function authError(err: unknown): string {
  if (err instanceof ApiRequestError) {
    switch (err.code) {
      case 'code_invalid':
        return "That code isn't right. Check the email we sent and try again.";
      case 'code_expired':
        return 'That code has expired. Send a new one.';
      case 'rate_limited':
        return 'Too many tries. Wait a few minutes, then try again.';
      case 'google_token_invalid':
        return "Google sign-in didn't go through. Try again or use your email.";
      default:
        return errorMessage(err);
    }
  }
  return errorMessage(err);
}

export function SignIn() {
  useTitle('Sign in');
  const { status, me, signIn } = useAuth();
  const { resolvedTheme } = usePrefs();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const now = useNow(1000);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  if (status === 'signedIn' && me) return <Navigate to={landingFor(me).path} replace />;

  const finish = (s: Session) => {
    signIn(s);
    navigate(landingFor(s.user).path, { replace: true });
  };

  const sendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    const em = email.trim().toLowerCase();
    if (!EMAIL_RE.test(em)) {
      setError('Enter the email address you use for PopCart.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.startEmailSignIn(em);
      setEmail(em);
      setResendAt(Date.now() + (r?.resendAfterSeconds ?? 30) * 1000);
      setCode('');
      setStep('code');
    } catch (err) {
      setError(authError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('The code is 6 digits.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      finish(await api.verifyEmailCode(email, code));
    } catch (err) {
      setError(authError(err));
      setBusy(false);
    }
  };

  const google = async (idToken: string) => {
    setBusy(true);
    setError(null);
    try {
      finish(await api.signInWithGoogle(idToken));
    } catch (err) {
      setError(authError(err));
      setBusy(false);
    }
  };

  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));

  return (
    <AuthPage heading>
      <h2 className="t-headline">Sign in</h2>

      {status === 'loading' ? (
        <p role="status">Checking your session…</p>
      ) : step === 'email' ? (
        <>
          {googleClientId ? (
            <>
              <GoogleButton onIdToken={google} dark={resolvedTheme === 'dark'} />
              <div className="divider">OR</div>
            </>
          ) : null}
          <form className="stack s12" onSubmit={sendCode} noValidate>
            <div className="field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'auth-err' : 'email-help'}
                required
              />
              <span id="email-help" className="help">
                We'll email you a 6-digit code.
              </span>
            </div>
            <button className="btn btn-primary btn-lg" type="submit" disabled={busy} aria-busy={busy || undefined}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          </form>
        </>
      ) : (
        <form className="stack s12" onSubmit={verify} noValidate>
          <p>
            We sent a code to <b>{email}</b>. It works for 10 minutes.
          </p>
          <div className="field">
            <label htmlFor="code">6-digit code</label>
            <input
              ref={codeRef}
              id="code"
              className="input code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'auth-err' : undefined}
            />
          </div>
          <button className="btn btn-primary btn-lg" type="submit" disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <div className="row between wrap">
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => {
                setStep('email');
                setError(null);
              }}
            >
              Use a different email
            </button>
            <button type="button" className="btn btn-quiet" disabled={busy || wait > 0} onClick={() => void sendCode()}>
              {wait > 0 ? `Send again in ${wait} s` : 'Send a new code'}
            </button>
          </div>
        </form>
      )}

      <div aria-live="assertive">
        {error ? (
          <p id="auth-err" className="pill st-error err-pill" style={{ whiteSpace: 'normal' }} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </AuthPage>
  );
}
